import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@design-system';
import { AdvisorOnboardingConsentStep, useConsentStep } from '@client/features/advisor-onboarding';
import { useAdvisorConsents } from '@client/hooks/useAdvisorConsents';
import { fetchSessionForAuthRequest } from '@client/model/sessionModel';
import { selectSession } from '@client/model/selectors';
import { toast } from 'sonner';

/**
 * Standalone consent page for advisors who need to agree to Terms of Service
 * outside of the onboarding flow.
 *
 * This is used when:
 * - An existing advisor hasn't agreed to updated Terms of Service
 * - The advisor has completed onboarding but TOS needs to be re-accepted
 */
export function AdvisorConsent({ advisorId }: AdvisorIdProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const consentStep = useConsentStep();
  const session = useSelector(selectSession);
  const [isWaitingForSession, setIsWaitingForSession] = useState(false);
  const { hasAgreedToTermsOfService, hasAgreedToSmsConsent, saveRequiredConsents, isSaving } =
    useAdvisorConsents({ advisorId });

  useEffect(() => {
    if (isWaitingForSession && session.forceRedirect !== '/consent') {
      setIsWaitingForSession(false);
      navigate('/');
    }
  }, [isWaitingForSession, session.forceRedirect, navigate]);

  const handleSubmit = useCallback(async () => {
    // Both checkboxes must be checked (or already saved)
    const hasBothConsents =
      (consentStep.hasConsentedToTos || hasAgreedToTermsOfService) &&
      (consentStep.hasConsentedToSms || hasAgreedToSmsConsent);

    if (!hasBothConsents) return;

    try {
      await saveRequiredConsents();
      // Refresh session to update consent flags, then wait for session to stabilize before navigating
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
      setIsWaitingForSession(true);
    } catch (error) {
      console.error('Failed to save consents:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error('Failed to save consents', {
        description: `${errorMessage}. Please try again.`,
      });
    }
  }, [
    consentStep.hasConsentedToTos,
    consentStep.hasConsentedToSms,
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
    saveRequiredConsents,
    dispatch,
  ]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="m-auto w-full max-w-2xl p-6">
        <AdvisorOnboardingConsentStep
          hasConsentedToTos={consentStep.hasConsentedToTos || hasAgreedToTermsOfService}
          hasConsentedToSms={consentStep.hasConsentedToSms || hasAgreedToSmsConsent}
          onTosConsentChange={consentStep.setHasConsentedToTos}
          onSmsConsentChange={consentStep.setHasConsentedToSms}
          isSubmitting={isSaving || isWaitingForSession}
          hasScrolledToBottom={consentStep.hasScrolledToBottom}
          onScrolledToBottom={consentStep.handleScrolledToBottom}
        />
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={
              !(
                (consentStep.hasConsentedToTos || hasAgreedToTermsOfService) &&
                (consentStep.hasConsentedToSms || hasAgreedToSmsConsent)
              ) ||
              isSaving ||
              isWaitingForSession
            }
          >
            {isSaving || isWaitingForSession ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
